import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import axios from 'axios';
import { MapPin, Calendar, Home } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Projects = () => {
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(searchParams.get('status') || 'all');

  useEffect(() => {
    fetchProjects();
  }, [activeFilter]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const url = activeFilter === 'all' ? `${API}/projects` : `${API}/projects?status=${activeFilter}`;
      const response = await axios.get(url);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const filters = [
    { value: 'all', label: 'All Projects' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'ongoing', label: 'Ongoing' },
    { value: 'completed', label: 'Completed' },
  ];

  const getStatusClass = (status) => {
    switch (status) {
      case 'upcoming':
        return 'status-upcoming';
      case 'ongoing':
        return 'status-ongoing';
      case 'completed':
        return 'status-completed';
      default:
        return 'bg-gray-200 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="projects-heading">
            Our Projects
          </h1>
          <p className="text-base text-gray-600 mb-8">Explore our range of residential developments</p>

          <div className="flex flex-wrap gap-3 mb-10">
            {filters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setActiveFilter(filter.value)}
                className={`px-6 py-3 rounded-md text-sm font-semibold transition-all uppercase letter-spacing-wide ${
                  activeFilter === filter.value
                    ? 'text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                }`}
                style={activeFilter === filter.value ? { background: 'linear-gradient(135deg, #C41E3A 0%, #8B2635 100%)' } : {}}
                data-testid={`filter-${filter.value}`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
              <p className="mt-4 text-gray-600">Loading projects...</p>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl" data-testid="no-projects">
              <p className="text-gray-600 text-lg">No projects found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="project-card bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl"
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="bg-gray-100 relative flex items-center justify-center" style={{ minHeight: '12rem' }}>
                    {project.images && project.images.length > 0 ? (
                      <img
                        src={project.images[0]}
                        alt={project.name}
                        className="w-full h-auto max-h-80 object-contain"
                      />
                    ) : (
                      <div className="w-full h-48 flex items-center justify-center">
                        <Home size={48} className="text-gray-400" />
                      </div>
                    )}
                    <span className={`status-badge ${getStatusClass(project.status)} absolute top-3 right-3`}>
                      {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{project.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                      <MapPin size={16} />
                      <span>{project.location_area}, {project.location_city}</span>
                    </div>
                    {project.apartment_types && project.apartment_types.length > 0 && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                        <Home size={16} />
                        <span>{project.apartment_types.join(', ')}</span>
                      </div>
                    )}
                    {project.expected_launch && project.status === 'upcoming' && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar size={16} />
                        <span>Launch: {project.expected_launch}</span>
                      </div>
                    )}
                    {project.possession_timeline && project.status === 'ongoing' && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar size={16} />
                        <span>Possession: {project.possession_timeline}</span>
                      </div>
                    )}
                    {project.year_completed && project.status === 'completed' && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar size={16} />
                        <span>Completed: {project.year_completed}</span>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Projects;